import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { Item, ItemTrigger } from 'designSystem/components/Item'
import { DocumentThumbnail } from 'features/knowledgeDocumentViewer/components/DocumentThumbnail'
import { MiddleTruncatedText } from 'features/knowledgeDocumentViewer/components/MiddleTruncatedText'
import { FavoriteDocument } from 'features/knowledgeDocumentViewer/utils/favoriteDocumentsStorage'
import { formatDocumentDisplayTitle } from 'features/knowledgeDocumentViewer/utils/textHelpers'
import { Star } from 'lucide-react'
import React from 'react'
import styled from 'styled-components'

const UnfavoriteButtonWrapper = styled.div`
  position: absolute;
  top: 0.375rem;
  right: 0.375rem;
  z-index: 1;
`

const TileBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  height: 100%;
  min-height: 0;
  gap: 0;
`

const ThumbnailArea = styled.div`
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
  border-radius: 0.375rem;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const TitleArea = styled.div`
  flex-shrink: 0;
  width: 100%;
  min-width: 0;
  padding: 0.375rem 0.25rem 0;
  font-size: 0.6875rem;
  font-weight: 500;
  line-height: 1.25rem;
  color: #fafafa;
`

interface FavoriteDocumentCardProps {
  document: FavoriteDocument
  isSelected?: boolean
  favoritesDisabled?: boolean
  favoritesSaving?: boolean
  onClick: (id: string, event?: React.MouseEvent) => void
  onRemoveFavorite: (id: string) => void
}

export const FavoriteDocumentCard: React.FC<FavoriteDocumentCardProps> = ({
  document,
  isSelected = false,
  favoritesDisabled = false,
  favoritesSaving = false,
  onClick,
  onRemoveFavorite,
}) => {
  const { t } = useLingui()
  const handleClick = (event: React.MouseEvent) => {
    onClick(document.id, event)
  }

  const handleRemove = (event: React.MouseEvent) => {
    event.stopPropagation()
    onRemoveFavorite(document.id)
  }

  const displayTitle = formatDocumentDisplayTitle(
    document.documentTitle,
    document.title
  )

  return (
    <Item size='tile' variant={isSelected ? 'surfaceActive' : 'surface'}>
      <UnfavoriteButtonWrapper>
        <Button
          type='button'
          tone='overlay'
          size='icon-xs'
          onClick={handleRemove}
          disabled={favoritesDisabled}
          aria-busy={favoritesSaving}
          title={t`Remove from favorites`}
          aria-label={t`Remove from favorites`}>
          <Star size={12} fill='currentColor' />
        </Button>
      </UnfavoriteButtonWrapper>
      <ItemTrigger
        type='button'
        aria-pressed={isSelected}
        onClick={handleClick}>
        <TileBody>
          <ThumbnailArea>
            <DocumentThumbnail
              documentId={document.systemDocumentEntryId}
              alt={displayTitle}
              filename={document.filename}
              bqDocumentId={document.bqDocumentId ?? document.id}
              cantileverDocumentEntryId={document.cantileverDocumentEntryId}
              documentHostsHydrated
              objectFit='cover'
            />
          </ThumbnailArea>
          <TitleArea>
            <MiddleTruncatedText
              text={displayTitle}
              maxWidth='100%'
              fillWidth
              tooltipFontSize='0.6875rem'
            />
          </TitleArea>
        </TileBody>
      </ItemTrigger>
    </Item>
  )
}
